import React, { useState, useRef, useEffect } from "react";
import { OverlayTrigger, Popover, Table } from "react-bootstrap";

const GAZ_DELIVERY_CATEGORY_NAMES = new Set([
  "бордюр",
  "бордюр дорожный",
  "брусчатка",
  "брусчатка и тротуарная плитка",
  "плитка тротуарная",
  "газонная решетка",
  "лоток водоотводный жби",
  "лоток водоотводный тротуарный",
]);

// Используется для отображения поддонов газобетона/факт.объёма — не трогаем, это
// про газобетон, а не про округление цены кирпича.
const isGazobetonCategory = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized.includes("газобетон") || GAZ_DELIVERY_CATEGORY_NAMES.has(normalized);
};

// Проблема округления цены до целого рубля перед умножением на количество актуальна
// только для кирпича (малая цена за штуку, партии в тысячи штук) — не для газобетона,
// у которого цена за штуку/блок высокая и такой переплаты не возникает. Поэтому узкая
// проверка именно по подкатегории material.category, а не по всей группе "Газобетон и кирпич".
const isBrickMaterial = (material) => String(material?.category || "").toLowerCase().includes("кирпич");

function MaterialsTooltip({
  materials,
  deliveryForTooltip,
  onRequestClick,
  isReagent = false,
  overrideTotalCost,
  izolifeComment,
  izolifePerPalletsComment,
  izolifePalletsComment,
  izolifePhrase,
  onDetailedTooltipMouseEnter,
  onDetailedTooltipMouseLeave
}) {
  const target = useRef(null);
  const [show, setShow] = useState(false);

  // Функция для формирования URL
  const getRequestUrl = (material) => {
    // Если есть guideId (из таблицы guide), используем его
    // if (material.guideId) {
    //   return `https://knowledgebase.mlt-development.ru/material/${material.guideId}/view`;
    // }
    // Иначе для "Общестрой" используем дефолтную ссылку
    if (material.calculator === "Общестрой") {
      return process.env.REACT_APP_SOURCE_STAS || "https://knowledgebase.mlt-development.ru/material/497/view";
    }
    return null;
  };

  // Вычисляем quantityNew и totalMaterialsCost
  const quantityNew = materials && materials.length > 0
    ? materials.map((material) => {
      if (isGazobetonCategory(material.category)) {
        const factVolume = material.factVolume && !isNaN(parseFloat(material.factVolume))
          ? parseFloat(material.factVolume)
          : null;
        return factVolume !== null ? factVolume.toLocaleString() : (material.quantity || 0).toLocaleString();
      }
      return (material.quantity || 1).toLocaleString();
    })
    : [];

  // Для кирпича цена за единицу мала, а берут его партиями в тысячи штук —
  // округление цены (или промежуточной суммы) до целого рубля даёт заметную переплату,
  // поэтому для него считаем без округления и показываем итог с копейками.
  const hasBrickMaterial = materials.some((m) => isBrickMaterial(m));

  const totalMaterialsCost = materials && materials.length > 0
    ? materials.reduce((sum, m) => {
      if (isGazobetonCategory(m.category)) {
        const factVolume = m.factVolume && !isNaN(parseFloat(m.factVolume))
          ? parseFloat(m.factVolume)
          : null;
        if (factVolume !== null) {
          return Math.ceil(sum + m.pricePerOne * factVolume);
        }
        return Math.ceil(sum + m.pricePerOne * m.quantity);
      }
      if (isBrickMaterial(m)) {
        return sum + m.pricePerOne * (m.quantity || 0);
      }
      return sum + Math.ceil(m.pricePerOne) * (m.quantity || 1);
    }, 0)
    : 0;

  const formatTotalCost = (value) => (
    hasBrickMaterial
      ? (Math.round((Number(value) || 0) * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : Math.ceil(Number(value) || 0).toLocaleString()
  );

  const formatRowSum = (quantityUsed, material) => (
    isBrickMaterial(material)
      ? (Math.round((quantityUsed || 0) * material.pricePerOne * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : Math.ceil((quantityUsed || 0) * Math.ceil(material.pricePerOne)).toLocaleString()
  );

  const formatPalletValue = (value) => {
    const numericValue = parseFloat(value);
    if (Number.isNaN(numericValue)) {
      return "-";
    }

    return Number.isInteger(numericValue)
      ? String(numericValue)
      : numericValue.toFixed(2).replace(/\.?0+$/, "");
  };

  const gazobetonPalletsData = materials
    .filter((material) => isGazobetonCategory(material?.category))
    .map((material) => {
      const occupiedPallets = Number.isFinite(parseFloat(material.occupiedPallets))
        ? parseFloat(material.occupiedPallets)
        : (Number.isFinite(parseFloat(material.numberPallets)) ? parseFloat(material.numberPallets) : null);
      const totalPallets = Number.isFinite(parseFloat(material.totalPallets))
        ? parseFloat(material.totalPallets)
        : null;

      return {
        name: material.name,
        totalPallets,
        occupiedPallets,
      };
    });

  const gazDetailsData = deliveryForTooltip?.gaz?.details?.data;
  const gazBreakdown = gazDetailsData?.deliveryBreakdown;
  const bestMethod = String(gazDetailsData?.bestMethod || "").toLowerCase();
  const shalandaCount = parseFloat(gazBreakdown?.shalandaCount);
  const manipulatorGoesWithShalands = gazBreakdown?.manipulatorGoesWithShalands === true;

  const parsedNumberOfTrips = parseFloat(gazDetailsData?.numberOfTrips);
  const numberOfTrips = Number.isFinite(parsedNumberOfTrips) && parsedNumberOfTrips > 0
    ? parsedNumberOfTrips
    : (gazobetonPalletsData.length > 0 ? 1 : null);

  const parsedShalandaCapacity = parseFloat(gazBreakdown?.shalandaCapacity);
  const parsedManipulatorCapacity = parseFloat(gazBreakdown?.manipulatorCapacity);

  const occupiedTotal = gazobetonPalletsData.reduce((sum, item) => {
    const value = parseFloat(item.occupiedPallets);
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);

  let totalCapacity = null;

  if (bestMethod.includes("манипулятор") && !bestMethod.includes("шаланда")) {
    totalCapacity =
      Number.isFinite(parsedManipulatorCapacity) &&
        parsedManipulatorCapacity > 0 &&
        Number.isFinite(numberOfTrips) &&
        numberOfTrips > 0
        ? numberOfTrips * parsedManipulatorCapacity
        : null;
  } else if (
    Number.isFinite(parsedShalandaCapacity) &&
    parsedShalandaCapacity > 0 &&
    Number.isFinite(numberOfTrips) &&
    numberOfTrips > 0
  ) {
    const baseShalandaCount =
      Number.isFinite(shalandaCount) && shalandaCount > 0 ? shalandaCount : numberOfTrips;

    totalCapacity = baseShalandaCount * parsedShalandaCapacity;

    // В сценарии "манипулятор едет вместе" добавляем его вместимость отдельно,
    // иначе tooltip занижает свободные поддоны.
    if (
      manipulatorGoesWithShalands &&
      Number.isFinite(parsedManipulatorCapacity) &&
      parsedManipulatorCapacity > 0
    ) {
      totalCapacity += parsedManipulatorCapacity;
    }
  }

  // Fallback для legacy-ответов: берем емкость из строк материалов.
  if (!Number.isFinite(totalCapacity) || totalCapacity <= 0) {
    const rowCapacities = gazobetonPalletsData
      .map((item) => parseFloat(item.totalPallets))
      .filter((value) => Number.isFinite(value) && value > 0);
    const fallbackCapacityPerTrip = rowCapacities.length > 0 ? rowCapacities[0] : null;

    totalCapacity =
      Number.isFinite(fallbackCapacityPerTrip) &&
        fallbackCapacityPerTrip > 0 &&
        Number.isFinite(numberOfTrips) &&
        numberOfTrips > 0
        ? fallbackCapacityPerTrip * numberOfTrips
        : null;
  }

  const freePalletsTotal =
    Number.isFinite(totalCapacity) && totalCapacity > 0
      ? Math.max(totalCapacity - occupiedTotal, 0)
      : null;

  // useEffect вызывается безусловно
  useEffect(() => {
    const handleResize = () => {
      if (show && target.current) {
        const targetRect = target.current.getBoundingClientRect();
        const popoverWidth = 800;
        const windowWidth = window.innerWidth;
        const scrollX = window.scrollX || window.pageXOffset;
        let placement = "right";
        let x = targetRect.right + scrollX;

        if (x + popoverWidth > windowWidth + scrollX) {
          placement = "left";
          x = targetRect.left + scrollX - popoverWidth;
        }

        if (x < scrollX) x = scrollX + 10;
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize);
    };
  }, [show]);

  // Проверка на отсутствие данных после всех хуков
  if (!materials || materials.length === 0) {
    return <span>Нет данных</span>;
  }

  const popover = (
    <Popover
      id="materials-popover"
      style={{
        maxWidth: "90vw",
        minWidth: "300px",
        maxHeight: "70vh",
        overflow: "auto",
      }}
    >
      <Popover.Header as="h5">Состав материалов</Popover.Header>
      <Popover.Body>
        <Table striped bordered hover size="sm" style={{ width: "100%", tableLayout: "auto" }}>
          <thead>
            <tr>
              <th style={{ whiteSpace: "nowrap" }}>Название</th>
              <th style={{ whiteSpace: "nowrap" }}>Кол-во</th>
              <th style={{ whiteSpace: "nowrap" }}>Цена</th>
              <th style={{ whiteSpace: "nowrap" }}>Сумма</th>
            </tr>
          </thead>
          <tbody>
            {materials.map((material, index) => (
              <tr key={index}>
                <td style={{ wordBreak: "break-word" }}>{material.name}</td>
                <td style={{ wordBreak: "break-word" }}>{quantityNew[index]}</td>
                <td style={{ wordBreak: "break-word" }}>
                  {material.pricePerOne === 0 ? (
                    (() => {
                      const url = getRequestUrl(material);
                      return url ? (
                        <span
                          style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline" }}
                          onClick={(e) => {
                            if (onRequestClick) {
                              onRequestClick(e);
                            } else {
                              e.stopPropagation();
                              window.open(url, "_blank");
                            }
                          }}
                        >
                          {isReagent ? "регламент" : "по запросу"}
                        </span>
                      ) : (
                        isReagent ? "регламент" : "по запросу"
                      );
                    })()
                  ) : (
                    isBrickMaterial(material)
                      ? (Math.round(material.pricePerOne * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : Math.ceil(material.pricePerOne).toLocaleString()
                  )}
                </td>
                <td style={{ wordBreak: "break-word" }}>
                  {material.quantity === null
                    ? 1 * material.pricePerOne === 0
                      ? (() => {
                        const url = getRequestUrl(material);
                        return url ? (
                          <span
                            style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline" }}
                            onClick={(e) => {
                              if (onRequestClick) {
                                onRequestClick(e);
                              } else {
                                e.stopPropagation();
                                window.open(url, "_blank");
                              }
                            }}
                          >
                            {isReagent ? "регламент" : "по запросу"}
                          </span>
                        ) : (
                          isReagent ? "регламент" : "по запросу"
                        );
                      })()
                      : formatRowSum(1, material)
                    : material.quantity * material.pricePerOne === 0
                      ? (() => {
                        const url = getRequestUrl(material);
                        return url ? (
                          <span
                            style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline" }}
                            onClick={(e) => {
                              if (onRequestClick) {
                                onRequestClick(e);
                              } else {
                                e.stopPropagation();
                                window.open(url, "_blank");
                              }
                            }}
                          >
                            {isReagent ? "регламент" : "по запросу"}
                          </span>
                        ) : (
                          isReagent ? "регламент" : "по запросу"
                        );
                      })()
                      : formatRowSum(material.quantity, material)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {gazobetonPalletsData.length > 0 && (
          <Table striped bordered hover size="sm" style={{ width: "100%", tableLayout: "auto", marginTop: "12px" }}>
            <thead>
              <tr>
                <th style={{ whiteSpace: "nowrap" }}>Поддоны (газобетон)</th>
                <th style={{ whiteSpace: "nowrap" }}>Занято</th>
              </tr>
            </thead>
            <tbody>
              {gazobetonPalletsData.map((item, index) => (
                <tr key={`pallets-${index}`}>
                  <td style={{ wordBreak: "break-word" }}>{item.name}</td>
                  <td>{formatPalletValue(item.occupiedPallets)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {gazobetonPalletsData.length > 0 && (
          <div style={{ marginTop: "8px", fontWeight: 600 }}>
            Кол-во свободных поддонов: {freePalletsTotal === null ? "-" : formatPalletValue(freePalletsTotal)}
          </div>
        )}
        {izolifeComment && (
          <div style={{ marginTop: "8px", fontSize: "13px", color: "#d97706", fontWeight: 500 }}>
            ⚠️ {izolifeComment}
            <div>{izolifePerPalletsComment}</div>
            <div>{izolifePalletsComment}</div>
          </div>
        )}
        {izolifePhrase && (
          <div style={{ marginTop: "4px", fontSize: "13px", color: "#d97706" }}>
            {izolifePhrase}
          </div>
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="right"
      flip
      overlay={popover}
      rootClose
      show={show}
      onToggle={setShow}
      popperConfig={{
        modifiers: [
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 10],
            },
          },
        ],
      }}
    >
      <span
        ref={target}
        onClick={() => {
          const opening = !show;
          setShow(opening);
          if (opening) onDetailedTooltipMouseLeave?.();
        }}
        onMouseEnter={() => !show && onDetailedTooltipMouseEnter?.()}
        onMouseLeave={() => onDetailedTooltipMouseLeave?.()}
        className={(isReagent || totalMaterialsCost === 0) ? "rd-prod-regulation-link" : undefined}
        style={(isReagent || totalMaterialsCost === 0)
          ? { cursor: "pointer" }
          : { cursor: "pointer", textDecoration: "underline", color: "#007bff" }}
      >
        {isReagent || totalMaterialsCost === 0 ? (
          materials.some(m => m.calculator === "Общестрой") ? (
            (() => {
              // Ищем материал с guideId или используем первый "Общестрой"
              const obshchestroyMaterial = materials.find(m => m.guideId) || materials.find(m => m.calculator === "Общестрой");
              const url = getRequestUrl(obshchestroyMaterial);
              return url ? (
                <span
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    if (onRequestClick) {
                      onRequestClick(e);
                    } else {
                      e.stopPropagation();
                      window.open(url, "_blank");
                    }
                  }}
                >
                  {isReagent ? "регламент" : "по запросу"}
                </span>
              ) : (
                isReagent ? "регламент" : "по запросу"
              );
            })()
          ) : (
            isReagent ? "регламент" : "по запросу"
          )
        ) : (
          overrideTotalCost != null
            ? Math.ceil(overrideTotalCost).toLocaleString()
            : formatTotalCost(totalMaterialsCost)
        )}
      </span>
    </OverlayTrigger>
  );
}

export default MaterialsTooltip;
